/**
 * Login domain resolution for SSO flows where JWT email and UPN domains differ
 * (e.g. Azure AD: preferred_username @.io, email @.com).
 */

export const LOGIN_DOMAIN_CONFIG_FOR_CALLBACK_KEY = 'login_domain_config_for_callback';

/** Survives hard refresh so we recreate UserManager with the same authority/client as login. */
export const OIDC_CONFIG_FOR_RESTORE_KEY = 'oidc_config_for_restore';

export type LoginDomainConfig = {
  domain: string;
  customerId?: string;
  customerName?: string;
  providerType?: string;
  hasSSO?: boolean;
  oidcConfig?: unknown;
  keycloakAlias?: string;
  authConfig?: unknown;
  auth_url?: string;
  ssoProvider?: string;
};

export type PersistedOidcConfig = {
  issuer: string;
  clientId: string;
  tokenEndpointPath?: string;
};

export function saveOidcConfigForRestore(oidcConfig: {
  issuer?: string;
  clientId?: string;
  tokenEndpointPath?: string;
} | null | undefined): void {
  if (typeof window === 'undefined' || !oidcConfig?.issuer || !oidcConfig?.clientId) {
    return;
  }
  try {
    const payload: PersistedOidcConfig = {
      issuer: oidcConfig.issuer.trim().replace(/\/+$/, ''),
      clientId: oidcConfig.clientId,
      tokenEndpointPath: oidcConfig.tokenEndpointPath,
    };
    localStorage.setItem(OIDC_CONFIG_FOR_RESTORE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function readOidcConfigForRestore(): PersistedOidcConfig | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(OIDC_CONFIG_FOR_RESTORE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedOidcConfig;
    if (!parsed?.issuer || !parsed?.clientId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearOidcConfigForRestore(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(OIDC_CONFIG_FOR_RESTORE_KEY);
  } catch {
    /* ignore */
  }
}

export function resolveLoginDomain(
  preferredUsername?: string | null,
  email?: string | null
): string | null {
  const upn = preferredUsername?.trim();
  const identifier =
    upn && upn.includes('@') ? upn : (email?.trim() || '');
  if (!identifier || !identifier.includes('@')) {
    return null;
  }
  return identifier.split('@')[1]?.toLowerCase() || null;
}

export function normalizeDomain(value?: string | null): string | null {
  if (!value || !value.trim()) {
    return null;
  }
  return value.trim().toLowerCase();
}

export function loginDomainConfigMatches(
  config: LoginDomainConfig | null | undefined,
  loginDomain: string | null
): boolean {
  if (!config || !loginDomain) {
    return false;
  }
  return normalizeDomain(config.domain) === normalizeDomain(loginDomain);
}

export function saveLoginDomainConfigForCallback(
  result: Record<string, unknown>,
  email: string
): void {
  if (typeof window === 'undefined') {
    return;
  }
  const emailDomain = email.split('@')[1] || '';
  const payload: LoginDomainConfig = {
    domain: (result.domain as string) || emailDomain,
    customerId: result.customerId as string | undefined,
    customerName: result.customerName as string | undefined,
    providerType: result.providerType as string | undefined,
    hasSSO: !!(
      result.providerType &&
      result.providerType !== 'local' &&
      result.oidcConfig
    ),
    oidcConfig: result.oidcConfig,
    keycloakAlias:
      (result.keycloakAlias as string | undefined) ||
      (result.oidcConfig as { idpAlias?: string } | undefined)?.idpAlias,
    authConfig: result.providerConfig ?? result.authConfig,
  };
  sessionStorage.setItem(
    LOGIN_DOMAIN_CONFIG_FOR_CALLBACK_KEY,
    JSON.stringify(payload)
  );
}

export function readLoginDomainConfigFromSessionStorage(): LoginDomainConfig | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const raw = sessionStorage.getItem(LOGIN_DOMAIN_CONFIG_FOR_CALLBACK_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as LoginDomainConfig;
  } catch {
    return null;
  }
}

export function clearLoginDomainConfigFromSessionStorage(): void {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.removeItem(LOGIN_DOMAIN_CONFIG_FOR_CALLBACK_KEY);
}

export function mapDiscoveryToDomainConfig(
  discovery: Record<string, unknown>,
  loginDomain: string
): LoginDomainConfig {
  return {
    customerId: discovery.customerId as string | undefined,
    customerName: discovery.customerName as string | undefined,
    domain: (discovery.domain as string) || loginDomain,
    providerType: discovery.providerType as string | undefined,
    hasSSO: !!(
      discovery.providerType &&
      discovery.providerType !== 'local'
    ),
    oidcConfig: discovery.oidcConfig,
    keycloakAlias: discovery.keycloakAlias as string | undefined,
    authConfig: discovery.providerConfig,
  };
}
